import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import apiService from '../../services/apiService'

const AdminStateTournamentRecords = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [availableYears, setAvailableYears] = useState([])
  const [users, setUsers] = useState([])
  const [selectedYear, setSelectedYear] = useState('')
  const [selectedUser, setSelectedUser] = useState('')
  const [tournaments, setTournaments] = useState([])
  const [searched, setSearched] = useState(false)

  useEffect(() => {
    loadInitialData()
  }, [])

  const loadInitialData = async () => {
    try {
      setInitialLoading(true)
      const response = await apiService.get('/client-admin/state-tournament-records')
      if (response.data.success) {
        const data = response.data.data
        setAvailableYears(data.available_years || [])
        setUsers(data.users || [])
        if (data.available_years?.length > 0) {
          setSelectedYear(data.available_years[0])
        }
      }
    } catch (error) {
      console.error('Error loading initial data:', error)
    } finally {
      setInitialLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!selectedYear || !selectedUser) return

    try {
      setLoading(true)
      setSearched(true)
      const response = await apiService.get('/client-admin/state-tournament-records', {
        params: { session_year: selectedYear, user_id: selectedUser }
      })
      if (response.data.success) {
        setTournaments(response.data.data.tournaments || [])
      }
    } catch (error) {
      console.error('Error fetching records:', error)
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }}></i>
          Loading...
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <i className="fas fa-trophy" style={{ marginRight: '12px' }}></i>
            State Tournament Records
          </h1>
          <p style={styles.subtitle}>
            View tournament participation and results by session and user
          </p>
        </div>
        <button onClick={() => navigate('/admin/dashboard')} style={styles.backBtn}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {/* Filter Section */}
      <div style={styles.filterCard}>
        <div style={styles.filterGrid}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>
              <i className="fas fa-calendar-alt" style={{ marginRight: '6px', color: '#c00f15' }}></i>
              Select Session
            </label>
            <select
              value={selectedYear}
              onChange={(e) => { setSelectedYear(e.target.value); setSearched(false); setTournaments([]) }}
              style={styles.filterSelect}
            >
              <option value="">-- Select Session --</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>
              <i className="fas fa-user" style={{ marginRight: '6px', color: '#c00f15' }}></i>
              Select User
            </label>
            <select
              value={selectedUser}
              onChange={(e) => { setSelectedUser(e.target.value); setSearched(false); setTournaments([]) }}
              style={styles.filterSelect}
            >
              <option value="">-- Select User --</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name}{user.state ? ` (${user.state})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={handleSearch}
              disabled={!selectedYear || !selectedUser || loading}
              style={{
                ...styles.searchBtn,
                cursor: (!selectedYear || !selectedUser || loading) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <><i className="fas fa-spinner fa-spin"></i> Searching...</>
              ) : (
                <><i className="fas fa-search"></i> Search</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div style={styles.loading}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }}></i>
          Fetching records...
        </div>
      ) : searched && tournaments.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>
            <i className="fas fa-search"></i>
          </div>
          <div style={styles.emptyText}>No Record Found</div>
          <div style={styles.emptySubtext}>
            No tournament records found for the selected session and user
          </div>
        </div>
      ) : tournaments.length > 0 ? (
        tournaments.map((record, idx) => (
          <div key={record.id || idx} style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.eventName}>{record.tournament_name}</h3>
            </div>

            <div style={styles.eventMeta}>
              {record.user_name && (
                <div style={styles.metaItem}>
                  <i className="fas fa-user" style={styles.metaIcon}></i>
                  <span>{record.user_name}</span>
                </div>
              )}
              {(record.venue || record.city) && (
                <div style={styles.metaItem}>
                  <i className="fas fa-map-marker-alt" style={styles.metaIcon}></i>
                  <span>{[record.venue, record.city].filter(Boolean).join(', ')}</span>
                </div>
              )}
              {record.formatted_dates && (
                <div style={styles.metaItem}>
                  <i className="fas fa-calendar-alt" style={styles.metaIcon}></i>
                  <span>{record.formatted_dates}</span>
                </div>
              )}
            </div>

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, ...styles.thFirst }}>Particulars</th>
                    <th style={styles.th}>Men</th>
                    <th style={styles.th}>Women</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ ...styles.td, ...styles.tdFirst }}>Total Participating Teams</td>
                    <td style={styles.td}>{record.total_teams_men || 0}</td>
                    <td style={styles.td}>{record.total_teams_women || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ ...styles.td, ...styles.tdFirst }}>Winner</td>
                    <td style={styles.td}>{record.winner_men || '-'}</td>
                    <td style={styles.td}>{record.winner_women || '-'}</td>
                  </tr>
                  <tr>
                    <td style={{ ...styles.td, ...styles.tdFirst }}>Runner Up</td>
                    <td style={styles.td}>{record.runner_up_men || '-'}</td>
                    <td style={styles.td}>{record.runner_up_women || '-'}</td>
                  </tr>
                  <tr>
                    <td style={{ ...styles.td, ...styles.tdFirst }}>3rd Place</td>
                    <td style={styles.td}>{record.third_place_men || '-'}</td>
                    <td style={styles.td}>{record.third_place_women || '-'}</td>
                  </tr>
                  <tr>
                    <td style={{ ...styles.td, ...styles.tdFirst }}>Joint 3rd Place</td>
                    <td style={styles.td}>{record.joint_third_place_men || '-'}</td>
                    <td style={styles.td}>{record.joint_third_place_women || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))
      ) : !searched ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>
            <i className="fas fa-trophy"></i>
          </div>
          <div style={styles.emptyText}>Select Session & User</div>
          <div style={styles.emptySubtext}>
            Choose a session year and user, then click Search to view records
          </div>
        </div>
      ) : null}
    </div>
  )
}

const styles = {
  container: {
    padding: '1.5rem 2rem',
    width: '100%',
    minHeight: '100vh',
    background: '#f3f4f6',
  },
  header: {
    background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 50%, #c00f15 100%)',
    padding: '1.5rem 2rem',
    color: 'white',
    borderRadius: '12px',
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  backBtn: {
    background: 'white',
    border: 'none',
    color: '#c00f15',
    borderRadius: '8px',
    padding: '10px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.85rem',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  title: {
    margin: '0 0 0.5rem 0',
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#ffffff',
  },
  subtitle: {
    margin: 0,
    fontSize: '1rem',
    color: '#ffffff',
  },
  filterCard: {
    background: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto',
    gap: '16px',
    alignItems: 'end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
  },
  filterSelect: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    fontSize: '0.95rem',
    fontWeight: '500',
    cursor: 'pointer',
    backgroundColor: '#fff',
    width: '100%',
    outline: 'none',
  },
  searchBtn: {
    padding: '10px 24px',
    backgroundColor: '#c00f15',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
    height: '42px',
  },
  card: {
    border: '2px solid #c00f15',
    borderRadius: '10px',
    marginBottom: '28px',
    overflow: 'hidden',
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
  },
  cardHeader: {
    backgroundColor: '#c00f15',
    color: '#fff',
    padding: '14px 20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  eventName: {
    fontSize: '17px',
    fontWeight: '700',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'white',
  },
  eventMeta: {
    padding: '10px 20px',
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    flexWrap: 'wrap',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e9ecef',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#555',
  },
  metaIcon: {
    color: '#c00f15',
    fontSize: '14px',
  },
  tableWrapper: {
    overflowX: 'auto',
    padding: '0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    padding: '10px 14px',
    textAlign: 'center',
    fontWeight: '700',
    fontSize: '13px',
    borderBottom: '2px solid #dee2e6',
    backgroundColor: '#fef2f2',
    color: '#1a1a2e',
  },
  thFirst: {
    textAlign: 'left',
    paddingLeft: '20px',
    width: '200px',
  },
  td: {
    padding: '9px 14px',
    textAlign: 'center',
    borderBottom: '1px solid #eee',
    color: '#333',
  },
  tdFirst: {
    textAlign: 'left',
    paddingLeft: '20px',
    fontWeight: '600',
    color: '#1a1a2e',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#6b7280',
    fontSize: '15px',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  empty: {
    textAlign: 'center',
    padding: '3rem',
    color: '#888',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  emptyIcon: {
    fontSize: '48px',
    color: '#ddd',
    marginBottom: '12px',
  },
  emptyText: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
  },
  emptySubtext: {
    fontSize: '13px',
    color: '#aaa',
    marginTop: '6px',
  },
}

export default AdminStateTournamentRecords
